import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from "react";
import { API } from "../Services/Api";
import { useAlert } from "../ViewHooks/AlertHook";
import { useLoader } from "../ViewHooks/LoaderHook";
import { useUsuario } from "../Hooks/UsuarioHook";

/** ===== Entidade ===== */
export interface ParcelaResumo {
  // Identificação
  registro: number;
  unico: number;

  // Pagador
  descricao: string;
  fone: string;
  cpf?: string | null;
  cnpj?: string | null;
  endereco?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;

  // Cobrança
  descr1: string;
  descr2: string;
  descr3: string;

  obs?: string | null;

  // Datas/valores
  vencimento: string | Date; // aceita Date do banco ou string ISO
  parcela: number;
  valor: number;

  // Boleto (opcional)
  linhaDigitavel?: string | null;   // 47 dígitos (exibir como texto)
  codigoBarras44?: string | null;   // 44 dígitos (gerar as barras)

  orientacoesPagamento?: string | null; // Orientações de pagamento (exibir como texto) - NOVO CAMPO
}


/** ===== Helpers ===== */
function extractData<T = any>(resp: any): T {
  return resp?.data?.data ?? resp?.data ?? resp;
}

/** ===== Contexto ===== */
interface GuiasContextData {
  guias: ParcelaResumo[];

  getGuiasPorCpfCnpj: (cpf_cnpj: string, banco: string) => Promise<ParcelaResumo[] | null>;

  clear: () => void;
}

const GuiasContext = createContext<GuiasContextData | undefined>(undefined);

export function useGuiasStorage() {
  const ctx = useContext(GuiasContext);
  if (!ctx) throw new Error("useGuiasStorage deve ser usado dentro de <GuiasProvider>");
  return ctx;
}

/** ===== Provider ===== */
export function GuiasProvider({ children }: { children: ReactNode }) {
  const [guias, setGuias] = useState<ParcelaResumo[]>([]);

  const { showLoader, hideLoader } = useLoader();
  const { addAlert } = useAlert();
  const { usuario } = useUsuario();

  const path = "/guias";

  const clear = () => {
    setGuias([]);
  };

  async function getGuiasPorCpfCnpj(cpf_cnpj: string, banco: string): Promise<ParcelaResumo[] | null> {
    try {
      setGuias([]);
      if (!usuario) {
        addAlert("Usuario inválido.");
        return null;
      }
      showLoader("Carregando guias...");
      // API espera pagina/tamanho no BODY → usar POST
      const resp = await API.post(`${path}/buscar-guias`, {
        banco: {
          banco: banco.replace("1-", "").replace("2-", ""),
          numero: banco.substring(0, 1),
        },
        cpf_cnpj: cpf_cnpj,
      });

      const data = extractData<ParcelaResumo>(resp);
      if (!data || !Array.isArray(data)) {
        addAlert("Resposta inesperada ao carregar guias.");
        return null;
      } else if (data.length == 0) {
        addAlert("Guias não encontradas para esse CPF/CNPJ.");
        return null;
      }

      setGuias(data);
      return data;
    } catch (e) {
      console.error(e);
      addAlert("Erro ao carregar guias.");
      return null;
    } finally {
      hideLoader();
    }
  }

  const value = useMemo<GuiasContextData>(() => ({
    guias,
    getGuiasPorCpfCnpj,
    clear,
  }), [guias]);

  return <GuiasContext.Provider value={value}>{children}</GuiasContext.Provider>;
}